/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { useState, useMemo, useCallback } from 'react';
import { getImage } from "gatsby-plugin-image";
import styled from '@emotion/styled';
import { useLocalization } from "gatsby-theme-i18n";
import { DateTime } from 'luxon';
import Program from './Program'
import Visual from './Visual'
import RoundButton from '../../../components/RoundButton';
import CardList from '../../../components/CardList';

import { useCurrentTime } from '../../../functions/useCurrentTime';
import { useIsClient } from '../../../functions/useIsClient';


import * as breakpoint from '../../../styles/breakpoints.module.scss';

const Timetable = ({ list, id, position, defaultVisual, type }) => {
  const { currentTime } = useCurrentTime();
  const { t, localeKey, currentLocaleObj } = useLocalization();

  const [ currentItem, setCurrentItem] = useState(defaultVisual);
  const [ popupItem, setPopupItem ] = useState(null);

  const { isClient, key } = useIsClient();

  
  const programsList = useMemo(() => {
    if(list)
    {
      let tempList = [];
      let elemList = [];
      const timezone = {
        'ja' : 'Asia/Tokyo',
        'fr' : 'Europe/Paris',
        'en_US' : 'utc-7',
        'en_GB' : 'gmt',
        'de' : 'utc+2'
      }

      const format = {
        'ja' : 'HH:mm',
        'fr' : 'HH:mm',
        'en_US' : 'hh:mm a',
        'en_GB' : 'HH:mm',
        'de' : 'HH:mm'
      }
      
      
      list.edges.forEach((item) => {
        if(item.node.data.type === "stage")
          tempList.push({
            category: '',
            title: item.node.title ? item.node.title[localeKey] : '',
            desc: item.node.desc ? item.node.desc[localeKey] : '',
            image: getImage(item.node.thumb),
            visual : getImage(item.node.visual),
            data: item.node.data,
            time: item.node.time ? item.node.time[localeKey] : '',
            isNew: DateTime.fromISO(item.node.data.update_date).diff(DateTime.local(), [ 'days']).values.days > -3,
            annotation : item.node.annotation ? item.node.annotation[localeKey] : '',
            guests : item.node.guests,
            performers : item.node.performers ? item.node.performers[localeKey] : '',
            streamingUrl : item.node.data.stream_url
          });
          
        const isLive = DateTime.fromISO(item.node.data.start_time) < currentTime && DateTime.fromISO(item.node.data.end_time) > currentTime;
        const slot = DateTime.fromISO(item.node.data.start_time).setZone(timezone[localeKey]).toFormat(format[localeKey]).replace('AM','a.m.').replace('PM','p.m.');
        const modalIndex = tempList.length - 1;

        elemList.push(
          <Program 
            slot={slot} 
            time={item.node.time[localeKey]} 
            title={item.node.title[localeKey]} 
            isLive={isLive} 
            id={item.node.data.key}
            type={item.node.data.type}
            key={`timeslot_${item.node.data.key}`}
            onMouseEnter={item.node.data.type === "stage" ? (e) => {
              setCurrentItem(item.node.data.key);
            } : null}
            onClick={item.node.data.type === "stage" ? (e) => {
              e.preventDefault();
              setPopupItem(modalIndex);
            } : null}
          />
        );
      });
      // console.log(data.programsList.edges);
      return { list : tempList, elem : elemList};
    } else {
      return [];
    }
  },[list,localeKey,currentTime  ]);
  
  const resetPopup = useCallback(() => {
    setPopupItem(null);
  },[setPopupItem]);

  if ( !isClient ) return null;
  
  return (<TimetableWrapper className={position}>
    {type !== "streaming" &&
      <Visual list={list} currentItem={currentItem}/>
    }
    <Table>
      {type !== "streaming" &&
        <TitleLabel>{t(`program_timetable.${id}.stage.title`)}</TitleLabel>
      }
      {programsList.elem}
      <RoundButton color={"#4E4E50"} size={"large"} isAnchor={true} to={`/${currentLocaleObj.prefix}/program#${id}`} css={css`
        margin:48px auto 0;
        height:auto;
        padding:16px 32px;

        @media only screen and (min-width: ${breakpoint.mdMin}){
          .streaming &{
            margin-top:28px;
            font-size: 16px;
            line-height: 24px;
            padding:12px 24px;
          }
        }
        @media only screen and (max-width: ${breakpoint.smMax}){
          font-size: 16px;
          line-height: 24px;
          padding:12px 24px;
          height:auto;
          margin-top:28px;
        }
        @media only screen and (max-width: ${breakpoint.xsMax}){
          font-size: 14px;
          line-height: 20px;
          padding:8px 16px;
          margin-top:32px;

         
        }
      `}>{t(`program_timetable.${id}.program.detail.button`)}</RoundButton>
    </Table>
    <CardList col={3} list={programsList.list} type={'modalOnly'} hvModal={true} currentItem={popupItem} onModalClose={resetPopup}/>
  </TimetableWrapper>);
}
 
export default Timetable;

const TimetableWrapper = styled.div`
  margin: 48px 0 0 0;
  display:flex;

  &.left{
    flex-direction: row-reverse;
  }

  .streaming &{
    margin:32px 0 28px;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    margin:32px 0 0;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    margin:24px 0 0;
  }

`
const TitleLabel = styled.div`
  color: #2C2C2E;
  font-size: 28px;
  line-height: 40px;
  text-align: center;
  letter-spacing: 0.03em;
  font-weight: 900;
  margin-bottom:48px;

  @media only screen and (max-width: ${breakpoint.smMax}){
    display:none;
  }
`

const Table = styled.div`
  text-align:center;
  margin-left:24px;
  width: calc(50% - 24px);

  &.left{
    margin-right:24px;
    margin-left:0;
  }

  .streaming &{
    width:100%;
    margin-left:0;
    margin-right:0;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    margin:0;
    width:100%;
  }
`
